import { useCallback, useMemo, useState } from 'react';
import { message, Modal, Tabs } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ExportOutlined,
  ClockCircleOutlined,
  RiseOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { buildInvestmentsColumns } from './investmentsColumns';
import { investmentsApi } from './investmentsApi';
import { friendlyErrorFor } from './investmentsHelpers';
import InvestmentModal from './InvestmentModal';
import InvestmentIncomeModal from './InvestmentIncomeModal';
import InvestmentDisposalModal from './InvestmentDisposalModal';
import InvestmentMaturityModal from './InvestmentMaturityModal';
import InvestmentValuationModal from './InvestmentValuationModal';
import InvestmentCategoriesPanel from './InvestmentCategoriesPanel';
import '../accounts.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Investments Register page (Stage 6B.9E).
//
// Two-tab page:
//   1. Investments  — grid + Add/Edit/View + Income/Dispose/Mature/Value/Delete
//   2. Categories   — simple master list
//
// Approval-MFA L1 is wired for Delete / Record Income / Dispose /
// Mature. Valuation is register-only (no MFA, no GL effect). The
// engine (voucherPosting.service) is the only code path that mutates
// ledger balances.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => investmentsApi.list(query) };

function defaultOnError(err) {
  message.error({ content: friendlyErrorFor(err), key: 'inv-list' });
}

function InvestmentsTab() {
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildInvestmentsColumns({ currentPage, pageSize }),
    [currentPage, pageSize]);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [modalOpen, setModalOpen]   = useState(false);
  const [modalMode, setModalMode]   = useState('add');
  const [modalRecord, setModalRecord] = useState(null);
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [incomeInv,  setIncomeInv]  = useState(null);
  const [dispOpen, setDispOpen]     = useState(false);
  const [dispInv,  setDispInv]      = useState(null);
  const [matOpen, setMatOpen]       = useState(false);
  const [matInv,  setMatInv]        = useState(null);
  const [valOpen, setValOpen]       = useState(false);
  const [valInv,  setValInv]        = useState(null);

  const openAdd = useCallback(() => {
    setModalMode('add'); setModalRecord(null); setModalOpen(true);
  }, []);

  const openWithFetch = useCallback(async (record, modeKind) => {
    try {
      const full = await investmentsApi.get(record.id);
      setModalMode(modeKind);
      setModalRecord(full);
      setModalOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const openEdit = useCallback((r) => openWithFetch(r, 'edit'), [openWithFetch]);
  const openView = useCallback((r) => openWithFetch(r, 'view'), [openWithFetch]);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const onSaved = useCallback(async () => {
    setModalOpen(false);
    await refetch();
  }, [refetch]);

  // ── Record Income ──────────────────────────────────────
  const handleIncome = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active investments can record income. This investment is ${record.status}.`);
      return;
    }
    try {
      const full = await investmentsApi.get(record.id);
      setIncomeInv(full);
      setIncomeOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const onIncomePosted = useCallback(async () => {
    setIncomeOpen(false); await refetch();
  }, [refetch]);

  // ── Dispose ────────────────────────────────────────────
  const handleDispose = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active investments can be disposed. This investment is ${record.status}.`);
      return;
    }
    try {
      const full = await investmentsApi.get(record.id);
      setDispInv(full);
      setDispOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const onDisposed = useCallback(async () => {
    setDispOpen(false); await refetch();
  }, [refetch]);

  // ── Mature ─────────────────────────────────────────────
  const handleMature = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active investments can be matured. This investment is ${record.status}.`);
      return;
    }
    try {
      const full = await investmentsApi.get(record.id);
      setMatInv(full);
      setMatOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const onMatured = useCallback(async () => {
    setMatOpen(false); await refetch();
  }, [refetch]);

  // ── Revalue (register-only) ────────────────────────────
  const handleRevalue = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active investments can be revalued. This investment is ${record.status}.`);
      return;
    }
    try {
      const full = await investmentsApi.get(record.id);
      setValInv(full);
      setValOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const onRevalued = useCallback(async () => {
    setValOpen(false); await refetch();
  }, [refetch]);

  // ── Delete ─────────────────────────────────────────────
  const handleDelete = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active investments without posted transactions can be deleted. This investment is ${record.status}.`);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete investment ${record.investmentCode}?`,
        icon:  <ExclamationCircleOutlined />,
        content: 'The investment will be soft-deleted. You will be asked for a 6-digit Approval MFA code to confirm. Investments with posted vouchers or transactions cannot be deleted — dispose them instead.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'accounts.investment.delete',
        resourceType:   'investment',
        resourceId:     record.id,
        operationLabel: 'Delete investment',
        resourceLabel:  `${record.investmentCode} · ${record.investmentName}`,
        action: (approvalMfaChallengeId) =>
          investmentsApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Investment deleted.');
      await refetch();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      message.error(friendlyErrorFor(e));
    }
  }, [refetch, runWithApproval]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: false }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',           label: 'View',            icon: <EyeOutlined />,         onClick: openView },
    { key: 'edit',           label: 'Edit',            icon: <EditOutlined />,        onClick: openEdit },
    { key: 'record-income',  label: 'Record Income',   icon: <DollarOutlined />,      onClick: handleIncome },
    { key: 'dispose',        label: 'Dispose',         icon: <ExportOutlined />,      onClick: handleDispose },
    { key: 'mature',         label: 'Mature',          icon: <ClockCircleOutlined />, onClick: handleMature },
    { key: 'revalue',        label: 'Update Value',    icon: <RiseOutlined />,        onClick: handleRevalue },
    { key: 'delete',         label: 'Delete',          icon: <DeleteOutlined />,
      danger: true, onClick: handleDelete }
  ]), [openView, openEdit, handleIncome, handleDispose, handleMature, handleRevalue, handleDelete]);

  return (
    <>
      <ErpDataGrid
        title="Investments"
        subtitle="Manage fixed deposits, mutual funds, shares, bonds and other investments"
        gridId="accounts-investments-v1"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={56}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchPlaceholder="Search by code, name, institution, folio…"
        searchShortcut
        exportable
        exportFilename="investments"
        actions={actionsProp}
        addButtonText="New Investment"
        emptyTitle="No investments yet"
        emptyText="Click New Investment to register your first investment."
        emptyCtaLabel="New Investment"
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        onRowClick={openView}
        contextMenuActions={contextMenuActions}
        {...gridHandlers}
      />
      <InvestmentModal
        open={modalOpen}
        mode={modalMode}
        record={modalRecord}
        onClose={closeModal}
        onSaved={onSaved}
      />
      <InvestmentIncomeModal
        open={incomeOpen}
        investment={incomeInv}
        onClose={() => setIncomeOpen(false)}
        onPosted={onIncomePosted}
        runWithApproval={runWithApproval}
      />
      <InvestmentDisposalModal
        open={dispOpen}
        investment={dispInv}
        onClose={() => setDispOpen(false)}
        onDisposed={onDisposed}
        runWithApproval={runWithApproval}
      />
      <InvestmentMaturityModal
        open={matOpen}
        investment={matInv}
        onClose={() => setMatOpen(false)}
        onMatured={onMatured}
        runWithApproval={runWithApproval}
      />
      <InvestmentValuationModal
        open={valOpen}
        investment={valInv}
        onClose={() => setValOpen(false)}
        onSaved={onRevalued}
      />
      {ApprovalMfaModalSlot}
    </>
  );
}

export default function InvestmentsPage() {
  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <Tabs
          size="small"
          defaultActiveKey="investments"
          items={[
            { key: 'investments', label: 'Investments', children: <InvestmentsTab /> },
            { key: 'categories',  label: 'Categories',  children: <InvestmentCategoriesPanel /> }
          ]}
        />
      </div>
    </ErpMasterShell>
  );
}
